import React, { useEffect, useMemo, useRef, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import './vinculo.scss'

/** Um item que pode ser escolhido no pop-up. */
export interface ItemVinculo {
  id: string
  nome: string
  sub?: string
  /** Texto extra só para a busca (empresa, e-mail, marca). */
  busca?: string
  /** Etiqueta ao lado do nome (ex.: a marca). */
  etiqueta?: string
  /** Agrupa a lista (ex.: o cliente da unidade, a tabela da coluna). */
  grupo?: string
  /** Já vinculado: aparece apagado e não pode ser marcado. */
  ja?: string
  /** Campo livre para os filtros da aba. */
  tipo?: string
}

export interface AbaVinculo {
  id: string
  rotulo: string
  dica: string
  unidade: [string, string]
  itens: ItemVinculo[]
  filtros?: { id: string; rotulo: string; teste: (i: ItemVinculo) => boolean }[]
}

export type Selecao = Record<string, ItemVinculo[]>

interface Props {
  aberto: boolean
  titulo: string
  sub?: string
  abas: AbaVinculo[]
  /** Campos acima da lista. */
  extra?: React.ReactNode | ((sel: Selecao) => React.ReactNode)
  /** Campos abaixo da lista e do que foi escolhido (grupo do menu, de onde copiar…): dependem do que a pessoa marcou, então vêm depois. */
  depois?: React.ReactNode | ((sel: Selecao) => React.ReactNode)
  /** A frase do resultado antes de confirmar. */
  resumo: (sel: Selecao, total: number) => React.ReactNode
  botao: (total: number) => string
  /** Grava; devolve a mensagem de erro, se houver, para o pop-up continuar aberto. */
  aoConfirmar: (sel: Selecao) => Promise<string | null>
  aoFechar: () => void
  carregando?: boolean
}

const MAXIMO_NA_LISTA = 120

function normalizar(t: string) {
  return t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

function plural(n: number, [um, varios]: [string, string]) {
  return `${n} ${n === 1 ? um : varios}`
}

/**
 * O pop-up de vínculo do portal novo: busca, filtros, seleção múltipla, a bandeja do que
 * foi escolhido e a frase do resultado antes de confirmar. O que já está vinculado
 * aparece apagado. Serve para acesso, equipe, membros, ambientes e dicionário.
 */
export default function VinculoDialog({ aberto, titulo, sub, abas, extra, depois, resumo, botao, aoConfirmar, aoFechar, carregando }: Props) {
  const [aba, setAba] = useState(abas[0]?.id)
  const [busca, setBusca] = useState('')
  const [filtro, setFiltro] = useState('')
  const [sel, setSel] = useState<Selecao>({})
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const campo = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!aberto) return
    setAba(abas[0]?.id); setBusca(''); setFiltro(''); setSel({}); setErro(null); setSalvando(false)
    setTimeout(() => campo.current?.focus(), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto])

  const atual = abas.find(a => a.id === aba) || abas[0]
  const escolhidos = sel[atual?.id] || []
  const total = Object.values(sel).reduce((s, l) => s + l.length, 0)

  const visiveis = useMemo(() => {
    if (!atual) return []
    const q = normalizar(busca.trim())
    const f = atual.filtros?.find(x => x.id === filtro)
    return atual.itens.filter(i => (!q || normalizar(`${i.nome} ${i.sub || ''} ${i.busca || ''} ${i.etiqueta || ''}`).includes(q)) && (!f || f.teste(i)))
  }, [atual, busca, filtro])

  if (!aberto || !atual) return null

  const marcado = (i: ItemVinculo) => escolhidos.some(x => x.id === i.id)
  const alternar = (i: ItemVinculo) => setSel(s => ({ ...s, [atual.id]: marcado(i) ? escolhidos.filter(x => x.id !== i.id) : [...escolhidos, i] }))
  const livres = visiveis.filter(i => !i.ja)
  const todosMarcados = livres.length > 0 && livres.every(marcado)
  const marcarTodos = () => setSel(s => ({
    ...s,
    [atual.id]: todosMarcados ? escolhidos.filter(x => !livres.some(l => l.id === x.id)) : [...escolhidos, ...livres.filter(l => !marcado(l))]
  }))
  const tirar = (abaId: string, id: string) => setSel(s => ({ ...s, [abaId]: (s[abaId] || []).filter(x => x.id !== id) }))

  const confirmar = async () => {
    setSalvando(true); setErro(null)
    const e = await aoConfirmar(sel)
    setSalvando(false)
    if (e) setErro(e)
  }

  let grupoAnterior: string | undefined
  return (
    <div className="nv-veu" onMouseDown={e => { if (e.target === e.currentTarget && !salvando) aoFechar() }}
         onKeyDown={e => { if (e.key === 'Escape' && !salvando) aoFechar() }}>
      <div className="nv" role="dialog" aria-modal="true" aria-labelledby="nv-titulo">
        <div className="nv-cab">
          <div><h2 id="nv-titulo">{titulo}</h2>{sub && <small>{sub}</small>}</div>
          <button type="button" className="nv-ico" onClick={aoFechar} aria-label="Fechar" disabled={salvando}><CloseIcon /></button>
        </div>
        <div className="nv-corpo">
          {typeof extra === 'function' ? extra(sel) : extra}
          {abas.length > 1 && (
            <div className="nv-abas" role="tablist">
              {abas.map(a => (
                <button key={a.id} type="button" role="tab" aria-selected={a.id === atual.id}
                        onClick={() => { setAba(a.id); setBusca(''); setFiltro('') }}>
                  {a.rotulo}{(sel[a.id] || []).length ? ` (${(sel[a.id] || []).length})` : ''}
                </button>
              ))}
            </div>
          )}
          <div className="nv-filtros">
            <label className="nv-busca"><SearchIcon fontSize="small" />
              <input ref={campo} value={busca} onChange={e => setBusca(e.target.value)} placeholder={atual.dica} aria-label={atual.dica} />
            </label>
            {atual.filtros && atual.filtros.length > 0 && (
              <div className="nv-chips" role="group" aria-label="Filtrar">
                {[{ id: '', rotulo: 'Todos' }, ...atual.filtros].map(f => (
                  <button key={f.id} type="button" aria-pressed={filtro === f.id} onClick={() => setFiltro(f.id)}>{f.rotulo}</button>
                ))}
              </div>
            )}
          </div>
          <div className="nv-linha">
            <span>{plural(visiveis.length, atual.unidade)}{busca.trim() || filtro ? ' encontrados' : ''}</span>
            {livres.length > 0 && <button type="button" onClick={marcarTodos}>{todosMarcados ? 'Desmarcar estes' : `Marcar os ${livres.length}`}</button>}
          </div>
          <div className="nv-lista" role="listbox" aria-multiselectable="true" aria-label={atual.rotulo}>
            {carregando && <p className="nv-vazio">Carregando…</p>}
            {!carregando && visiveis.length === 0 && <p className="nv-vazio">Nada encontrado com essa busca.</p>}
            {visiveis.slice(0, MAXIMO_NA_LISTA).map(i => {
              const cab = i.grupo && i.grupo !== grupoAnterior ? i.grupo : null
              grupoAnterior = i.grupo
              const on = marcado(i)
              return (
                <React.Fragment key={i.id}>
                  {cab && <div className="nv-grupo">{cab}</div>}
                  <button type="button" role="option" aria-selected={on} disabled={!!i.ja} onClick={() => alternar(i)}>
                    <span className={`nv-ck${on ? ' on' : ''}`} aria-hidden="true" />
                    <span className="nv-nome"><b>{i.nome}{i.etiqueta && <span className="nv-etiqueta">{i.etiqueta}</span>}</b>{i.sub && <small>{i.sub}</small>}</span>
                    {i.ja && <span className="nv-ja">{i.ja}</span>}
                  </button>
                </React.Fragment>
              )
            })}
            {visiveis.length > MAXIMO_NA_LISTA && <p className="nv-vazio">Mostrando {MAXIMO_NA_LISTA} de {visiveis.length}. Use a busca para achar os outros.</p>}
          </div>
          {total > 0 && (
            <div className="nv-bandeja">
              <span className="nv-rotulo">Escolhidos ({total})</span>
              <div className="nv-escolhidos">
                {abas.flatMap(a => (sel[a.id] || []).map(i => (
                  <button key={`${a.id}-${i.id}`} type="button" onClick={() => tirar(a.id, i.id)} aria-label={`Tirar ${i.nome}`}>{i.nome}<span aria-hidden="true">×</span></button>
                )))}
              </div>
            </div>
          )}
          {typeof depois === 'function' ? depois(sel) : depois}
          <div className="nv-resumo">{resumo(sel, total)}</div>
          {erro && <div className="nv-erro" role="alert">{erro}</div>}
        </div>
        <div className="nv-rodape">
          <button type="button" className="nv-btn" onClick={aoFechar} disabled={salvando}>Cancelar</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="nv-btn prim" onClick={confirmar} disabled={!total || salvando}>{salvando ? 'Salvando…' : botao(total)}</button>
        </div>
      </div>
    </div>
  )
}
